import React, { useState } from "react";
import {
  Button,
  Grid,
  Paper,
  TextField,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import { DatePicker } from "@mui/x-date-pickers";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";

import FileUpload from "../File/FileUpload";
import FileItem from "../File/FileItem";
import IndicatorList from "./IndicatorList";
import IndicatorModalForm from "./IndicatorModalForm";
import { analysisSchema } from "../../validations/AgreementValidation";
import { createAnalyzeAgreement, analyzeReport } from "../../services/agreementServices";
import { handleApiError } from "../../utils/errorHandler";
import { createIndicator } from "../../services/indicatorServices";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";




export default function AgreementAnalyzeForm({ id, agreement_type }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiReports, setAiReports] = useState([]);
  const [selectedReportIndex, setSelectedReportIndex] = useState(null);
  const [loadingIA, setLoadingIA] = useState(false);
  const [indicators, setIndicators] = useState([]);
  const [indicator, setIndicator] = useState({});
  const [open, setOpen] = useState(false);
  const [formEdit, setFormEdit] = useState(false);
  const [indicatorsError, setIndicatorsError] = useState(false);

  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || 1;
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || "all";

  const handleCancel = () => {
    navigate(`/agreements?page=${page}&search=${search}&status=${status}`);
  };

  const initialValues = {
    academic_unit: "",
    career: "",
    coordinators: [{ name: "" }],
    proposal: "",
    evaluated_period: "",
    evaluation_unit: "",
    presentation_date: null,
    observation: "",
    files: null,
  };

  const addFiles = (newFiles) => {
    const filesWithIds = newFiles.map((f) => ({ ...f, __id: crypto.randomUUID(), preview: URL.createObjectURL(f) }));
    setFiles((prev) => [...prev, ...filesWithIds]);
  };

  const removeFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.__id !== fileId));
  };

  const initEditForm = (indicator) => {
    setOpen(true);
    setFormEdit(true);
    setIndicator(indicator);
  };

  return (
    <Paper elevation={0} sx={{ padding: 4 }}>
      <Formik
        initialValues={initialValues}
        validationSchema={analysisSchema}
        onSubmit={async (values) => {
          if (agreement_type === "Específico" && indicators.length === 0 && !aiSummary) {
            setIndicatorsError(true);
            return;
          }
          try {
            await createAnalyzeAgreement(id, values, files);
            toast.success("Se ha analizado el convenio correctamente");
            navigate(`/agreements?page=${page}&search=${search}&status=${status}&refresh=${Date.now()}`);
          } catch (err) {
            handleApiError(err, "Error al guardar los datos");
          }
        }}
      >
        {(props) => {
          const applyIaData = async (report) => {
            if (!report) return toast.error("No hay reporte seleccionado");

            const normalizeStatus = (status) => {
              if (!status) return "unexecuted"; // fallback
              const s = status.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
              if (s === "ejecutado") return "executed";
              if (s === "no ejecutado") return "unexecuted";
              return "unexecuted"; // fallback
            };

            try {
              setLoadingIA(true);

              // --- Campos del formulario ---
              props.setFieldValue("academic_unit", report.academic_unit || "");
              props.setFieldValue("career", report.career || "");
              props.setFieldValue("proposal", report.proposal || "");
              props.setFieldValue("evaluated_period", report.evaluated_period || "");
              props.setFieldValue("evaluation_unit", report.evaluation_unit || "");
              props.setFieldValue("presentation_date", report.presentation_date || null);
              props.setFieldValue("observation", report.observation || "");

              // --- Coordinadores ---
              const coordinatorsArray = Array.isArray(report.coordinators)
                ? report.coordinators
                : Object.values(report.coordinators || []);

              props.setFieldValue(
                "coordinators",
                coordinatorsArray.length
                  ? coordinatorsArray.map((c) => ({ name: c.name || "" }))
                  : [{ name: "" }]
              );

              // --- Crear indicadores en BD ---
              if (agreement_type === "Específico") {
                const indicatorsArray = Array.isArray(report.indicators)
                  ? report.indicators
                  : Object.values(report.indicators || []);

                if (indicatorsArray.length) {
                  const createdIndicators = [];
                  for (const ind of indicatorsArray) {
                    const payload = {
                      name: ind.name || "",
                      description: ind.description || "",
                      status: normalizeStatus(ind.status),
                      agreement_id: id
                    };
                    const res = await createIndicator(id, payload);
                    createdIndicators.push(res.data);
                  }
                  setIndicators(createdIndicators);
                  setIndicatorsError(false);
                }

                toast.success("Los indicadores se agregaron correctamente");
              }
               toast.success("Los campos se auto-completaron correctamente");
            } catch (error) {
              toast.error("Error al aplicar datos de IA");
              console.error(error);
            } finally {
              setLoadingIA(false);
            }
          };


          const handleSummarize = async () => {
            if (!files.length) return toast.error("Debe seleccionar un archivo primero.");
            const file = files[0].file || files[0];

            try {
              setLoadingIA(true);
              const res = await analyzeReport(file);
              setAiSummary(res.data.summary);
              const reports = res.data.data.reports || [];
              setAiReports(reports);
            } catch (error) {
              toast.error("Error generando resumen con IA");
            } finally {
              setLoadingIA(false);
            }
          };

          return (
            <Form>
              <Grid container spacing={2}>
                {/* Columna izquierda */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    {/* Seleccionar reporte IA */}
                    {aiReports.length > 0 && (
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="select-report-label">Elegir reporte IA</InputLabel>
                          <Select
                            labelId="select-report-label"
                            value={selectedReportIndex ?? ""}
                            onChange={(e) => setSelectedReportIndex(e.target.value)}
                          >
                            {aiReports.map((r, idx) => (
                              <MenuItem key={idx} value={idx}>
                                {r.academic_unit} - {r.career}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() => applyIaData(aiReports[selectedReportIndex || 0])}
                          disabled={loadingIA}
                        >
                          Aplicar datos de IA
                        </Button>
                      </Grid>
                    )}

                    {/* Campos del formulario */}
                    <Grid item xs={6}>
                      <Field as={TextField} name="academic_unit" label="Unidad académica *" size="small" fullWidth variant="outlined" error={!!props.errors.academic_unit && props.touched.academic_unit} helperText={<ErrorMessage name="academic_unit" />} />
                    </Grid>
                    <Grid item xs={6}>
                      <Field as={TextField} name="career" label="Carrera *" size="small" fullWidth variant="outlined" error={!!props.errors.career && props.touched.career} helperText={<ErrorMessage name="career" />} />
                    </Grid>

                    {/* Coordinadores */}
                    <Grid item xs={12}>
                       <FieldArray name="coordinators">
                  {({ push, remove, form }) => {
                    const { values, errors, touched } = form;
                    const { coordinators } = values;

                    return (
                      <>
                        {coordinators.map((_, index) => {
                          const isLast = index === coordinators.length - 1;
                          return (
                            <Grid
                              container
                              spacing={1}
                              alignItems="center"
                              key={index}
                              sx={{ mb: 1 }}
                            >
                              <Grid item xs={9}>
                                <Field
                                  name={`coordinators[${index}].name`}
                                  as={TextField}
                                  size="small"
                                  fullWidth
                                  label="Nombre del coordinador/designado *"
                                  error={
                                    !!errors?.coordinators?.[index]?.name &&
                                    !!touched?.coordinators?.[index]?.name
                                  }
                                  helperText={
                                    <ErrorMessage
                                      name={`coordinators[${index}].name`}
                                    />
                                  }
                                />
                              </Grid>
                              <Grid item xs={3} >
                                {isLast ? (
                                  <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<AddCircleIcon />}
                                    onClick={() => push({ name: "" })}
                                    sx={{ height: "41px" }}
                                  >
                                    Agregar
                                  </Button>
                                ) : (
                                  coordinators.length > 1 && (
                                    <IconButton
                                      onClick={() => remove(index)}
                                      color="error"
                                      aria-label="Eliminar"
                                      sx={{ mt: 1 }}
                                    >
                                      <RemoveCircleIcon />
                                    </IconButton>
                                  )
                                )}
                              </Grid>
                            </Grid>
                          );
                        })}
                      </>
                    );
                  }}
                </FieldArray>
              </Grid>

                    {/* Otros campos */}
                    <Grid item xs={6}><Field as={TextField} name="proposal" label="Celebrando a propuesta de *" fullWidth size="small" variant="outlined" error={!!props.errors.proposal && props.touched.proposal} helperText={<ErrorMessage name="proposal" />} /></Grid>
                    <Grid item xs={6}><Field as={TextField} name="evaluated_period" label="Periodo evaluado *" fullWidth size="small" variant="outlined" error={!!props.errors.evaluated_period && props.touched.evaluated_period} helperText={<ErrorMessage name="evaluated_period" />} /></Grid>
                    <Grid item xs={6}><Field as={TextField} name="evaluation_unit" label="Unidad evaluadora *" fullWidth size="small" variant="outlined" error={!!props.errors.evaluation_unit && props.touched.evaluation_unit} helperText={<ErrorMessage name="evaluation_unit" />} /></Grid>
                    <Grid item xs={6}>
                      <DatePicker value={props.values.presentation_date} onChange={(v) => props.setFieldValue("presentation_date", v)} format="DD/MM/YYYY" slotProps={{ textField: { fullWidth: true, size: "small", label: "Fecha de presentación *", error: !!props.errors.presentation_date && props.touched.presentation_date, helperText: <ErrorMessage name="presentation_date" /> } }} />
                    </Grid>
                    <Grid item xs={6}><Field as={TextField} name="observation" label="Observación *" fullWidth size="small" variant="outlined" error={!!props.errors.observation && props.touched.observation} helperText={<ErrorMessage name="observation" />} /></Grid>

                    {/* Archivos */}
                    <Grid item xs={12}>
                      <FileUpload setFieldValue={props.setFieldValue} setFiles={setFiles} files={files} />
                      <FileItem files={files} removeFile={removeFile} displayDeleteButton />
                      <Button variant="outlined" onClick={handleSummarize} disabled={!files.length || loadingIA} sx={{ mt: 2 }}>
                        {loadingIA ? "Generando..." : "Generar resumen con IA"}
                      </Button>
                    </Grid>

                    {/* Indicadores */}
                    {agreement_type === "Específico" && (
                      <>
                        <IndicatorList id={id} name="indicators" indicators={indicators} showButtons onHandleEdit={initEditForm} setIndicators={setIndicators} />
                        {indicatorsError && <Typography color="error">Debe agregar al menos un indicador</Typography>}
                        <IndicatorModalForm
                          setIndicators={setIndicators}
                          id={id}
                          open={open}
                          setOpen={setOpen}
                          title={formEdit ? "Editar indicador" : "Agregar indicador"}
                          initialValues={formEdit ? indicator : { name: "", status: "", description: "" }}
                          edit={formEdit}
                          indicatorId={indicator.id}
                          setFormEdit={setFormEdit}
                          setIndicatorsError={setIndicatorsError}
                        />
                      </>
                    )}
                  </Grid>
                </Grid>

                {/* Columna derecha */}
                <Grid item xs={12} md={4}>
                  {aiSummary && (
                    <Card sx={{ bgcolor: "#f9f9f9", borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Resumen IA</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{aiSummary}</Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                {/* Botones final */}
                <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
                  <Grid item><Button variant="contained" type="submit">Guardar</Button></Grid>
                  <Grid item><Button variant="outlined" onClick={handleCancel}>Cancelar</Button></Grid>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
}
